/**
 * ApplianceLicenceBanner — the licence term on a customer-hosted appliance.
 *
 * Hosted PRISM has no /api/appliance/licence (it 404s), so this renders nothing there. On an appliance
 * it stays silent until the last 14 days, then says when read-only begins; once read-only it says what
 * still works. Each state carries an icon and words, never colour alone.
 */
import { useEffect, useState } from 'react';
import { Alert, AlertTitle } from '@mui/material';
import apiClient from '../services/api';

type LicenceStatus = {
  mode: 'active' | 'expiring' | 'read_only';
  days_remaining?: number | null;
  expires_at?: string | null;
  reason?: string | null;
};

const day = (iso?: string | null) => (iso ? iso.slice(0, 10) : null);

export default function ApplianceLicenceBanner() {
  const [status, setStatus] = useState<LicenceStatus | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get('/appliance/licence')
      .then(r => {
        if (!cancelled) setStatus(r.data?.data ?? null);
      })
      .catch(() => {
        if (!cancelled) setStatus(null); // not an appliance, or not reachable: say nothing
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!status || status.mode === 'active') return null;

  if (status.mode === 'expiring') {
    const n = status.days_remaining ?? 0;
    return (
      <Alert severity="warning" role="alert" sx={{ mb: 2 }}>
        <AlertTitle>Licence term ending</AlertTitle>
        {n} {n === 1 ? 'day' : 'days'} left. From {day(status.expires_at)} this appliance becomes read-only: you
        will still be able to sign in, view and export your records, but not change anything. Contact Risk
        Aperture to renew.
      </Alert>
    );
  }

  const ended = day(status.expires_at);
  return (
    <Alert severity="error" role="alert" sx={{ mb: 2 }}>
      <AlertTitle>This appliance is read-only</AlertTitle>
      {ended ? `The licence term ended on ${ended}. ` : `${status.reason ? `${status.reason[0].toUpperCase()}${status.reason.slice(1)}. ` : ''}`}
      You can sign in, view and export your records; nothing can be created or changed. A renewed licence from
      Risk Aperture restores full use.
    </Alert>
  );
}
